import { INS_Ticker24hr } from '@/lib/api/innovation'
import { INS_Ticker24hr_Res, TickerDatum, Ins_TickerArray, INS_HeaderInfo, InsWSTicker24 } from '@/types/covestAPI/ins'
import { InsHeaderInfo } from '@/types/ins'
import { throttle } from '@/utils/datetimes'
import { create } from 'zustand'
import { storeSpotCtrl } from '../spotctrl'
import { isInsView } from '@/lib/settings'
import { ConvetDotSize } from '@/utils/formatter'
import { INS_GetCoinTickSize, INS_GetPriceTickSize } from './exchange'

const defaultInsHeader: InsHeaderInfo = {
  symbol: '',
  price: '',
  persent: '',
  diff: '',
  high: '',
  low: '',
  volumeUSDT: '',
  volume: '',
  markPrice: '',
}

const defaultInsTicker = {
  isInsConnected: false,
  InsretryCount: 0,
  InswsSocket: null,
  InsTicker24Map: new Map<string, TickerDatum>(),
  InsTicker24Arr: [] as Ins_TickerArray[],
  InsSymbol: 'COEUSDT',
  InscurPrice: '',
  InscurPriceTick: '',
  InscurCoinTick: '',
  InsHeaderInfo: defaultInsHeader,
  curMaker: false,
}

interface InterfaceInsTicker {
  isInsConnected: boolean
  InsretryCount: number
  InswsSocket: WebSocket | null
  InsTicker24Map: Map<string, TickerDatum>
  InsTicker24Arr: Ins_TickerArray[]
  InsSymbol: string
  InscurPrice: string
  InscurPriceTick: string
  InscurCoinTick: string
  InsHeaderInfo: InsHeaderInfo | null
  curMaker: boolean

  InsTickerInit: () => void
  APIInsTicker: () => void
  setInsSymbol: (symbol: string) => void
  setInsTicker24: (args: TickerDatum[]) => void
  setInsTicker24Arr: (args: TickerDatum[]) => void
  setWSTicker24Map: (args: InsWSTicker24) => void
  getInsHeaderForTicker: (symbol: string) => InsHeaderInfo | null
  wsInit: () => void
  wsOpen: (socket: WebSocket) => void
  wsClose: () => void
  wsError: (error: string) => void
  wsMessage: (data: any) => void
  setCurMaker: (maker: boolean) => void
}

function subscribeTicker(webSocket: WebSocket) {
  const message = {
    Cmd: 'TI24H!All',
  }
  webSocket.send(JSON.stringify(message))
}

export const storeInsTicker = create<InterfaceInsTicker>((set, get) => ({
  ...defaultInsTicker,

  InsTickerInit: () => {
    // :TODO INS 관련 데이터 막기
    if (!isInsView()) return
    get().APIInsTicker()
  },

  APIInsTicker: async () => {
    // :TODO INS 관련 데이터 막기
    if (!isInsView()) return
    const res: INS_Ticker24hr_Res = await INS_Ticker24hr('')
    get().setInsTicker24(res.TickerData)
    get().setInsTicker24Arr(res.TickerData)
  },

  setInsSymbol: (symbol: string) => {
    const header = get().getInsHeaderForTicker(symbol)
    if (header !== null) {
      set({ InsSymbol: symbol, InsHeaderInfo: header })
      return
    }
    set({ InsSymbol: symbol })
  },

  setInsTicker24: (args: TickerDatum[]) => {
    const data = new Map<string, TickerDatum>()
    args?.forEach((el) => {
      if (el.Symbol.endsWith('USDT')) {
        data.set(el.Symbol, el)
      }
    })

    const InscurPrice = String(data.get(storeSpotCtrl.getState().spotSymbol)?.CloseP)

    set({ InsTicker24Map: data, InscurPrice })
  },

  setInsTicker24Arr: (args: TickerDatum[]) => {
    const tmp: Ins_TickerArray[] = []
    const tmpHeader: InsHeaderInfo = { ...defaultInsHeader }
    args?.forEach((el) => {
      if (el.Symbol.endsWith('USDT')) {
        tmp.push({
          symbol: el.Symbol,
          price: String(el.CloseP),
          rate: el.CloseP !== 0 && el.OpenP !== 0 ? ((el.CloseP / el.OpenP) * 100).toFixed(2) : '0',
          quoteVolume: String(el.Vol),
          Favorites: false,
        })
      }
      // if (el.Symbol === get().InsSymbol) {
      const InsSymbol = storeSpotCtrl.getState().spotSymbol
      if (el.Symbol === InsSymbol) {
        const tick = INS_GetPriceTickSize(el.Symbol)
        tmpHeader.symbol = el.Symbol
        tmpHeader.price = ConvetDotSize(String(el.CloseP), tick)
        tmpHeader.persent = el.CloseP !== 0 && el.OpenP !== 0 ? (((el.CloseP - el.OpenP) / el.OpenP) * 100).toFixed(2) : '0'
        tmpHeader.diff = ConvetDotSize(String(el.CloseP - el.OpenP), tick)
        tmpHeader.high = String(el.HighP)
        tmpHeader.low = String(el.LowP)
        tmpHeader.volumeUSDT = ConvetDotSize(String(el.QVol), 2)
        tmpHeader.volume = String(el.Vol)
        tmpHeader.markPrice = ConvetDotSize(String(el.CloseP), tick)
      }
    })
    set({ InsTicker24Arr: tmp, InsHeaderInfo: tmpHeader })
  },

  setWSTicker24Map: throttle((args: InsWSTicker24) => {
    if (!args.Tickers?.length) return

    const tikcermap = new Map(get().InsTicker24Map)

    args.Tickers.forEach((el) => {
      tikcermap.set(el.s, {
        Symbol: el.s,
        OTime: el.ot,
        CTime: el.ct,
        OpenP: el.o,
        CloseP: el.c,
        HighP: el.h,
        LowP: el.l,
        Vol: el.v,
        QVol: el.q,
      })
    })
    
    const tickerArray = Array.from(tikcermap.values())
    get().setInsTicker24(tickerArray)
    get().setInsTicker24Arr(tickerArray)
  }, 500),

  getInsHeaderForTicker: (symbol: string): InsHeaderInfo | null => {
    const tick = INS_GetPriceTickSize(symbol)
    const map24 = get().InsTicker24Map
    if (!map24) return null
    const el = map24.get(symbol)
    if (!el) return null
    return {
      symbol: el.Symbol,
      price: String(el.CloseP),
      persent: el.CloseP !== 0 && el.OpenP !== 0 ? (((el.CloseP - el.OpenP) / el.OpenP) * 100).toFixed(2) : '0',
      diff: ConvetDotSize(String(el.CloseP - el.OpenP), tick),
      high: String(el.HighP),
      low: String(el.LowP),
      volumeUSDT: ConvetDotSize(String(el.QVol), 2),
      volume: String(el.Vol),
      markPrice: String(el.CloseP),
    }
  },

  wsInit: () => {
    if (!isInsView()) return
    if (get().InswsSocket) {
      console.log('[WS Ins] is already connected')
      return
    }

    const socket = new WebSocket(`${process.env.NEXT_PUBLIC_INNOVATION_MARKET_SOCKET_URL}`)

    socket.onopen = () => get().wsOpen(socket)
    socket.onclose = () => get().wsClose()
    socket.onerror = (event) => get().wsError((event as ErrorEvent).message)
    socket.onmessage = (event) => get().wsMessage(event.data)

    set({ InswsSocket: socket, isInsConnected: true, InsretryCount: 0 })
  },

  wsOpen: (socket: WebSocket) => {
    subscribeTicker(socket)
    console.log('[WS Ins] connected')
  },

  wsClose: () => {
    const socket = get().InswsSocket

    if (socket) {
      // WebSocket 연결 닫기
      socket.close()

      // 이벤트 리스너들 해제
      socket.onclose = null
      socket.onerror = null
      socket.onmessage = null

      set({
        InswsSocket: null, // WebSocket 객체를 null로 설정
        isInsConnected: false, // 연결 상태 false로 설정
        InsretryCount: 0, // 재시도 카운트 초기화
      })
      console.log('[WS Ins] unconnected')
    }
  },

  wsError: (error: string) => {
    set({ isInsConnected: false, InswsSocket: null })
    const retryLimit = 5
    const currentRetries = get().InsretryCount || 0
    if (currentRetries < retryLimit) {
      setTimeout(() => {
        set({ InsretryCount: currentRetries + 1 })
        get().wsInit()
      }, 1000)
    }
  },

  wsMessage: (message: string) => {
    const data = JSON.parse(message)
    if (Array.isArray(data.Tickers)) {
      get().setWSTicker24Map(data as InsWSTicker24)
    }
  },

  setCurMaker: (maker: boolean) => {
    set({ curMaker: maker })
  },
}))
